import { useMemo } from "react";
import { Box, BoxProps, InternalStandardProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { GiftDashboardListItem } from "./GiftDashboardListItem";
import { DashboardActivity } from "JS/Models/Firebase/GiftDashboard";
import { GiftDashboardFilters } from "JS/Models";
import { sortArrayByDate } from "JS/Helpers";
import { AppTypography } from "JS/React/Components/AppTypography";
import { messaging } from "JS/Helpers/UserMessaging";

const StyledRoot = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
}));

const StyledListWrapper = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
}));

const StyledEmptyList = styled(AppTypography)(({ theme }) => ({
    marginTop: theme.spacing(3),
    color: theme.palette.grey[500],
}));

const StyledDateHeader = styled(Box)(({ theme }) => ({
    color: theme.colors.white,
    backgroundColor: theme.palette.grey[900],
    display: "flex",
    alignItems: "center",
    height: "40px",
    paddingLeft: "10px",
}));

export const GiftDashboardListing = (props: GiftDashboadListingProps) => {
    const { className, giftDashboardLogs, activeFilter, apiError, ...rest } =
        props;

    const noContent =
        giftDashboardLogs && Object.keys(giftDashboardLogs)?.length === 0;

    const sortedGiftDashboardlogs = useMemo(() => {
        if (activeFilter === GiftDashboardFilters.BYDATE) {
            return (
                giftDashboardLogs &&
                Object.keys(giftDashboardLogs)?.sort((a, b) => {
                    return Date.parse(b) - Date.parse(a);
                })
            );
        } else if (
            [
                GiftDashboardFilters.BYTYPE,
                GiftDashboardFilters.BYMEMBER,
            ].includes(activeFilter)
        ) {
            return giftDashboardLogs && Object.keys(giftDashboardLogs).sort();
        } else {
            return giftDashboardLogs && Object.keys(giftDashboardLogs);
        }
    }, [giftDashboardLogs, activeFilter]);

    return (
        <StyledRoot className={className} {...rest}>
            <StyledListWrapper>
                {noContent && !apiError && (
                    <StyledEmptyList align="center" variant="h6">
                        {messaging.common.noItemsFound}
                    </StyledEmptyList>
                )}
                {noContent && apiError && (
                    <StyledEmptyList align="center" variant="h6">
                        {messaging.apiError.somethingWentWrong}
                    </StyledEmptyList>
                )}
                {sortedGiftDashboardlogs?.map((item, idx) => {
                    const data = giftDashboardLogs[item];
                    return (
                        <ListItemWithDateHeader
                            key={`${idx}-${item}`}
                            dashboradLogs={data}
                            createdDate={item}
                        />
                    );
                })}
            </StyledListWrapper>
        </StyledRoot>
    );
};

const ListItemWithDateHeader = (props: ListItemWithDateHeaderProps) => {
    const { dashboradLogs, createdDate } = props;

    const sortedDashbardLogs = useMemo(() => {
        return sortArrayByDate(dashboradLogs, "createdAt", "DESC");
    }, [dashboradLogs]);

    return (
        <>
            <StyledDateHeader>{createdDate}</StyledDateHeader>
            {sortedDashbardLogs?.map((x, idx) => (
                <GiftDashboardListItem
                    key={`${x.nid}-${x.createdAt}`}
                    giftActivity={x}
                />
            ))}
        </>
    );
};

export interface DashboradLogs extends DashboardActivity {
    createdDate: string;
    receiverFullName: string;
}

export interface GiftDashboadListingProps
    extends InternalStandardProps<BoxProps> {
    giftDashboardLogs: Record<string, DashboradLogs[]>;
    activeFilter: GiftDashboardFilters;
    apiError: boolean;
}

export interface ListItemWithDateHeaderProps
    extends InternalStandardProps<BoxProps> {
    dashboradLogs: DashboradLogs[];
    createdDate: string;
}
